import {
    CreatePatientFormValidationSchema,
    PatientDisability,
    PatientExtended,
    PatientFormValidationSchema,
    PatientOccupation,
    PatientQualification,
    PatientsColumn,
    PatientTaxCodeType,
    PatientTransportationMode
} from './types'
import {capitalize, ObjectEntries, ObjectKeys} from '@/utilities/helpers'
import {formatDate} from '@/dayjs'
import {FieldNamesMarkedBoolean} from 'react-hook-form'
import {z} from 'zod'
import {TKey} from '@/translations/i18n.tsx'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'

// maps
export const patientTaxCodeTypeToLabel = {
    fiscal_code: 'commons:fiscal_code',
    stp_code: 'patients:stp_code',
    team_card: 'patients:team_card',
    temporary_code: 'patients:temporary_code'
} as const satisfies Record<PatientTaxCodeType, TKey>

export const patientEducationToLabel = {
    bachelor_degree: 'commons:qualification_bachelor_degree',
    university_diploma: 'commons:qualification_university_diploma',
    master_degree: 'commons:qualification_master_degree',
    university_master: 'commons:qualification_university_master',
    doctorate: 'commons:qualification_doctorate',
    high_school_diploma: 'commons:qualification_high_school_diploma',
    middle_school_diploma: 'commons:qualification_middle_school_diploma',
    elementary_license: 'commons:qualification_elementary_license',
    old_system_degree: 'commons:qualification_old_system_degree',
    vocational_school_diploma: 'commons:qualification_vocational_school_diploma',
    not_declared: 'commons:qualification_not_declared',
    none: 'commons:none'
} as const satisfies Record<PatientQualification, TKey>

export const patientOccupationToLabel = {
    employed: 'patients:occupation_employed',
    homemaker: 'patients:occupation_homemaker',
    other_condition: 'patients:occupation_other_condition',
    seeking_first_job: 'patients:occupation_seeking_first_job',
    student: 'patients:occupation_student',
    unemployed: 'patients:occupation_unemployed',
    not_declared: 'commons:qualification_not_declared'
} as const satisfies Record<PatientOccupation, TKey>

export const patientTransportationModesToLabel = {
    own_vehicle: 'patients:own_vehicle',
    provider_vehicle: 'patients:provider_vehicle',
    asl_vehicle: 'patients:asl_vehicle',
    local_authority_vehicle: 'patients:local_authority_vehicle',
    other: 'commons:other'
} as const satisfies Record<PatientTransportationMode, TKey>

export const patientDisabilitiesToLabel = {
    none: 'commons:none_female',
    recognized: 'commons:recognized_female',
    requested: 'commons:request'
} as const satisfies Record<PatientDisability, TKey>

export const patientContactLabelsMapToLabel = {
    personal: 'patients:contact_personal',
    consort: 'patients:contact_consort',
    partner: 'patients:contact_partner',
    caregiver: 'patients:contact_caregiver',
    tutor: 'patients:contact_tutor',
    social_worker: 'commons:almoner',
    uncle_aunt: 'patients:contact_uncle_aunt',
    cousin: 'patients:contact_cousin',
    grandparent: 'patients:contact_grandparent',
    grandchild: 'patients:contact_grandchild',
    mother: 'patients:contact_mother',
    father: 'patients:contact_father',
    son: 'patients:contact_son',
    brother: 'patients:contact_brother',
    sister: 'patients:contact_sister'
} as const satisfies Record<string, TKey>

export const columnNameToLabel = {
    [PatientsColumn.enum.fullName]: 'commons:name_header',
    [PatientsColumn.enum.birthDate]: 'commons:birth_date_label',
    [PatientsColumn.enum.age]: 'commons:age',
    [PatientsColumn.enum.serviceType]: 'commons:service',
    [PatientsColumn.enum.outRegion]: 'commons:out_region',
    [PatientsColumn.enum.residenceAslDistrictId]: 'commons:residence_asl',
    [PatientsColumn.enum.residenceCityId]: 'commons:residence_city',
    [PatientsColumn.enum.residenceProvinceId]: 'commons:residence_province',
    [PatientsColumn.enum.neuromuscularDiagnosisId]: 'patients:neuromuscular_neurological_diagnosis',
    [PatientsColumn.enum.mobilePhone]: 'commons:mobile_phone',
    [PatientsColumn.enum.telephone]: 'commons:telephone',
    [PatientsColumn.enum.email]: 'commons:email',
    [PatientsColumn.enum.projectStatus]: 'commons:pri'
} as const satisfies Record<Exclude<PatientsColumn, 'select' | 'moreActions'>, TKey | null>

// remappers
export const getPatientDirtyFieldsFromFormValues = (
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<PatientFormValidationSchema>>>,
    formValues: Partial<PatientFormValidationSchema>
): NonNullable<Partial<PatientFormValidationSchema>> => {
    if (typeof dirtyFields != 'object' || dirtyFields == null || !formValues) {
        return {}
    }

    const dirtyFieldsKeys = ObjectKeys(dirtyFields)

    /** The 'contacts' field (using useFieldArray) causes the entire form to
     be marked as modified when adding elements. To prevent this, we create an exception." */
    if (dirtyFieldsKeys.includes('contacts')) {
        return {
            contacts: formValues['contacts']
        }
    }

    if (dirtyFieldsKeys.includes('residence')) {
        return {
            residenceAslDistrict: formValues['residenceAslDistrict'],
            residence: formValues['residence']
        }
    }

    if (dirtyFieldsKeys.includes('domicile')) {
        return {
            domicile: formValues['domicile'],
            domicileAslDistrict: formValues['domicileAslDistrict']
        }
    }

    return dirtyFieldsKeys.reduce((acc, key) => {
        const isDirty = dirtyFields[key]
        const value = formValues[key]

        return isDirty
            ? {
                  ...acc,
                  [key]: value
              }
            : acc
    }, {})
}

export const remapPatientToDefaultValues = (patient: PatientExtended) => {
    const remappedPatient = Object.fromEntries(
        ObjectEntries(patient).map(entry => {
            if (!entry) {
                throw new Error('patient entry is falsy')
            }

            const [key, value] = entry

            switch (key) {
                case 'birthDate': {
                    return ['birthDate', new Date(patient.birthDate)]
                }
                case 'consentDateFm01': {
                    return ['consentDateFm01', patient.consentDateFm01 && new Date(patient.consentDateFm01)]
                }
                case 'domicile': {
                    return [
                        'domicile',
                        patient.domicile
                            ? {
                                  address: patient.domicile.address,
                                  city: patient.domicile.city,
                                  streetNumber: patient.domicile.streetNumber,
                                  postalCode: patient.domicile.cap,
                                  municipality: patient.domicile.municipality ?? null,
                                  province: {acronym: patient.domicile.city.provinceAcronym}
                              }
                            : null
                    ]
                }
                case 'domicileAslDistrict': {
                    return [
                        'domicileAslDistrict',
                        patient.domicileAslDistrict?.id
                            ? {
                                  id: patient.domicileAslDistrict?.id,
                                  name: patient.domicileAslDistrict?.name,
                                  asl: {
                                      id: patient.domicileAslDistrict?.aslId,
                                      name: patient.domicileAslDistrict?.aslName
                                  }
                              }
                            : null
                    ]
                }
                case 'legalProtection': {
                    return ['legalProtection', patient.legalProtection ? 'subjectTo' : 'notSubjectTo']
                }
                case 'localAuthorityAssistanceService': {
                    return [
                        'localAuthorityAssistanceService',
                        patient.localAuthorityAssistanceService ? 'provided' : 'notProvided'
                    ]
                }
                case 'protectedCategory': {
                    return ['protectedCategory', patient.protectedCategory ? 'belonging' : 'notBelonging']
                }
                case 'residence': {
                    return [
                        'residence',
                        patient.residence
                            ? {
                                  address: patient.residence.address,
                                  city: patient.residence.city,
                                  streetNumber: patient.residence.streetNumber,
                                  postalCode: patient.residence.cap,
                                  latitude: patient?.residence?.latitude,
                                  longitude: patient?.residence?.longitude,
                                  municipality: patient.residence.municipality ?? null,
                                  province: {acronym: patient.residence.city.provinceAcronym}
                              }
                            : null
                    ]
                }
                case 'residenceAslDistrict': {
                    return [
                        'residenceAslDistrict',
                        patient.residenceAslDistrict?.id
                            ? {
                                  id: patient.residenceAslDistrict?.id,
                                  name: patient.residenceAslDistrict?.name,
                                  asl: {
                                      id: patient.residenceAslDistrict?.aslId,
                                      name: patient.residenceAslDistrict?.aslName
                                  }
                              }
                            : null
                    ]
                }
                default: {
                    return [key, value]
                }
            }
        })
    )

    return {
        ...remappedPatient,
        birthProvince: patient.birthCity ? {acronym: patient.birthCity.provinceAcronym} : null,
        isBornAbroad: patient?.birthCountry?.name != 'Italia',
        isDifferentDomicile:
            patient?.residence?.id != patient?.domicile?.id && patient.residence != null && patient.domicile != null
    }
}

export const remapPatientToPayload = (
    formValues: NonNullable<Partial<CreatePatientFormValidationSchema>>,
    skipNullish?: boolean
) => {
    return Object.fromEntries(
        ObjectEntries(formValues)
            .map(entry => {
                if (!entry) {
                    throw new Error('patient entry is falsy')
                }

                const [key, value] = entry

                switch (key) {
                    case 'lastName': {
                        return ['lastName', value ? capitalize(value) : null]
                    }

                    case 'firstName': {
                        return ['firstName', value ? capitalize(value) : null]
                    }

                    case 'birthCity': {
                        return ['birthCityId', value?.id ?? null]
                    }
                    case 'birthCountry': {
                        return ['birthCountryId', value?.id]
                    }
                    case 'birthDate': {
                        return ['birthDate', value && formatDate(value.toISOString(), 'YYYY-MM-DD')]
                    }
                    case 'citizenship': {
                        return ['citizenshipId', value?.id]
                    }
                    case 'consentDateFm01': {
                        return ['consentDateFm01', value && formatDate(value.toISOString(), 'YYYY-MM-DD')]
                    }
                    case 'contacts': {
                        return [
                            'contacts',
                            value && value?.length > 0
                                ? value.map(c => ({
                                      id: c?.id,
                                      label: c.label,
                                      type: c.type,
                                      contact: c.contact
                                  }))
                                : []
                        ]
                    }
                    case 'domicile': {
                        return [
                            'domicile',
                            value?.address
                                ? {
                                      address: value?.address,
                                      city: value?.city?.name,
                                      postalCode: value?.postalCode,
                                      municipalityId: value?.municipality?.id,
                                      latitude: value?.latitude,
                                      longitude: value?.longitude,
                                      streetNumber: value?.streetNumber || 'snc'
                                  }
                                : null
                        ]
                    }
                    case 'domicileAslDistrict': {
                        return ['domicileAslDistrictId', value?.id ?? null]
                    }
                    case 'legalProtection': {
                        return ['legalProtection', value == 'subjectTo']
                    }
                    case 'localAuthorityAssistanceService': {
                        return ['localAuthorityAssistanceService', value == 'provided']
                    }
                    case 'neuromuscularDiagnosis': {
                        return ['neuromuscularDiagnosisId', value?.id || null]
                    }
                    case 'protectedCategory': {
                        return ['protectedCategory', value == 'belonging']
                    }
                    case 'residence': {
                        return [
                            'residence',
                            value?.address
                                ? {
                                      address: value?.address,
                                      city: value?.city?.name,
                                      postalCode: value?.postalCode,
                                      municipalityId: value?.municipality?.id,
                                      latitude: value?.latitude,
                                      longitude: value?.longitude,
                                      streetNumber: value?.streetNumber || 'snc'
                                  }
                                : null
                        ]
                    }
                    case 'residenceAslDistrict': {
                        return ['residenceAslDistrictId', value?.id ?? null]
                    }
                    case 'notificationsRecipients': {
                        return ['notificationRecipientIds', value?.map(r => r.id) ?? null]
                    }
                    default: {
                        return [key, value]
                    }
                }
            })
            .filter(entry => {
                const [, value] = entry
                const isEmptyArray = Array.isArray(value) && value.length == 0
                const isEmptyObject = typeof value == 'object' && value != null && ObjectKeys(value).length == 0
                const shouldKeepValue = !!(value || typeof value == 'boolean')
                return !skipNullish || (shouldKeepValue && !isEmptyArray && !isEmptyObject)
            })
    )
}

export const usePatientsSearchParams = () =>
    useParsedSearchParams(
        z.object({
            limit: z.coerce.number().int().positive().default(50),
            page: z.coerce.number().int().positive().default(1),
            search: z.string().trim().optional(),
            filters: z.string().optional(),
            view: z.string().min(1).default('all'),
            sorts: z.string().optional(),
            columnVisibility: z.string().optional()
        })
    )
